// ============================================
// Morning Routine Planner Overlay
// Extracted from main.js
// ============================================

import { storyManager } from '../engine/story.js';
import { variableManager } from '../engine/variables.js';
import { layoutManager } from './layout.js';
import { events, Events } from '../engine/events.js';
import { L } from '../systems/localization.js';

// Callback for auto-selecting an exit choice after planner closes.
// Set via plannerUI.setAutoSelectCallback() from main.js.
let _autoSelectExitChoiceFn = null;

// F9 (2026-05-26): mirror do_action's Comfort/mood/HRT adjustment so the
// planner's per-toggle "(-5E)" label and summary line match the actual
// Energy that will be subtracted post-Start-Day. Pre-fix the planner
// showed raw base cost while the inline choice badge showed the
// effective cost — two surfaces, two numbers. See Globals.ink:2164
// `function do_action`. Arousal/drunk aren't applied here because the
// morning entry-point typically has Arousal=0 and is_drunk=false.
function _effectiveEnergyCost(base) {
    const story = storyManager.currentStory;
    if (!story) return base;
    const comfort = variableManager.getTotalValue('Comfort') || 0;
    const moodSwing = story.variablesState?.hrt_mood_swing || 0;
    const moodExtra = (moodSwing === 3) ? 2 : 0;
    let adjusted = base - comfort + moodExtra;
    if (adjusted < 3) adjusted = 3;
    return adjusted;
}

// P4: Morning Routine Panel (matches DailyPlannerController.cs)
function _showPlannerOverlay() {
    const story = storyManager.currentStory;
    if (!story) return;

    // Read current toggle states from localStorage
    const getToggle = (key) => localStorage.getItem(`Routine_${key}`) === '1';
    const toggles = {
        shower: getToggle('auto_shower'),
        makeup: getToggle('auto_makeup'),
    };

    const vs = story.variablesState;
    const ivar = (k) => (typeof vs[k] === 'number' ? vs[k] : 0);
    const bvar = (k) => !!vs[k];

    // Check locks
    const hasVanity = ivar('upgrade_vanity') >= 1;
    const hasMakeupSkill = ivar('makeup_skill') >= 1;
    const hasMakeupOn = bvar('has_makeup_on');
    // Match the Ink choice gate: `{upgrade_vanity >= 1 && not has_makeup_on
    // && not is_dirty && makeup_skill >= 1}`. Without the not-has_makeup_on
    // check, the planner shows the makeup toggle as enabled on a return
    // pass through MorningRoutine even though the Ink choice itself is gone.
    const canMakeup = hasVanity && hasMakeupSkill && !hasMakeupOn;
    const isDirty = bvar('is_dirty');

    // Auto cage-post toggle eligibility (Owner route).
    // Visibility: caged + Master rule introduced + OnlyFems installed.
    // Toggle-enabled: cage_post_streak >= 1 (player has posted manually at
    // least once — adyyxy's UX guard so the first run goes through the
    // narrative flow). Toggle state itself reads from the Ink VAR so it's
    // set-and-forget across reloads.
    const cagePostVisible = bvar('wearing_chastity') && ivar('master_intensity') >= 2
        && bvar('sms_master_cage_post_intro') && bvar('onlyfems_installed');
    const cagePostUnlocked = cagePostVisible && ivar('cage_post_streak') >= 1;
    const cagePostOn = cagePostUnlocked && bvar('auto_cage_post');

    // Outfit presets (wardrobe.js MAX_PRESETS=5, persisted to localStorage)
    let presets = [];
    try {
        const raw = localStorage.getItem('PnO_WardrobePresets');
        if (raw) presets = JSON.parse(raw) || [];
    } catch(e) { presets = []; }
    const savedPresets = presets.filter(p => p && p.items);

    // Planner always opens at the morning beat. Acts as the
    // single morning summary: agenda + outfit presets + automation toggles.
    // Start Day chains everything + exits to Apartment; Close just dismisses
    // the overlay and leaves the MorningRoutine hub for manual control.

    // Build overlay
    let overlay = document.getElementById('overlay-planner');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'overlay-planner';
        overlay.className = 'overlay hidden';
        overlay.innerHTML = '<div class="overlay-box planner-box"></div>';
        document.body.appendChild(overlay);
    }
    const box = overlay.querySelector('.overlay-box');
    const day = variableManager.getValue('Day') || 1;

    const _pTitle = L.get('planner.morning_routine');
    const _pShower = L.get('planner.shower');
    const _pMakeup = L.get('planner.makeup');
    const _pClean = L.get('planner.clean');
    const _pNeedVanity = L.get('planner.need_vanity');
    const _pStartDay = L.get('planner.start');
    const _pClose = L.get('planner.close');
    const _pCloseHintRaw = L.get('planner.close_hint');
    const _pCloseHint = _pCloseHintRaw !== 'planner.close_hint' ? _pCloseHintRaw : 'Comes back next morning';
    const _dayLabel = L.get('desktop.day');

    // Cage-post row label + lock reason
    const _pCage = L.get('planner.auto_cage_post');
    const _pCageLabel = _pCage !== 'planner.auto_cage_post' ? _pCage : 'Auto-post cage photo to OnlyFems';
    const _pCageNeedManual = L.get('planner.cage_need_manual');
    const _pCageNeedManualText = _pCageNeedManual !== 'planner.cage_need_manual' ? _pCageNeedManual : 'post once manually first';

    // Build agenda lines (read-only "today's notes")
    const _agendaLines = [];
    if (cagePostVisible && !bvar('cage_post_done_today')) {
        _agendaLines.push(cagePostOn
            ? '\ud83d\udcf8 Cage post: auto-on'
            : '\ud83d\udcf8 Cage post due today');
    }
    // Shift-counter only relevant if the player already worked at least once
    // today \u2014 tells them they have 1 left before the daily cap. If they haven't
    // worked yet, the morning agenda doesn't need to nag about a non-event.
    const shiftsToday = ivar('shifts_today');
    if (shiftsToday > 0 && shiftsToday < 2) {
        _agendaLines.push(`\ud83d\udcbc Work shifts: ${shiftsToday}/2 today`);
    }
    const weekday = ((ivar('Day') - 1) % 7) + 1; // 1=Mon ... 6=Sat, 7=Sun
    if ((weekday === 6 || weekday === 7) && bvar('girlfriend_accepts_fem') && !bvar('girlfriend_weekend_done_today')) {
        _agendaLines.push('\ud83d\udc95 Weekend: girlfriend visit available');
    }
    if (bvar('hrt_active')) {
        _agendaLines.push('\ud83d\udc8a HRT: active');
    }
    if (bvar('allowance_active')) {
        _agendaLines.push(`\ud83d\udcb0 Allowance cap: $500`);
    }

    box.innerHTML = `
        <div class="overlay-header">
            <h2>${_pTitle !== 'planner.morning_routine' ? _pTitle : 'Morning Routine'} \u2014 ${_dayLabel !== 'desktop.day' ? _dayLabel.replace('{0}', day) : 'Day ' + day}</h2>
            <button class="btn-overlay-close" id="planner-close">&times;</button>
        </div>
        <div class="planner-content">
            ${_agendaLines.length > 0 ? `
            <div class="planner-section planner-agenda" style="margin-bottom:8px;padding:6px 10px;background:rgba(196,112,224,0.08);border-radius:6px;font-size:12px;line-height:1.6;">
                ${_agendaLines.map(l => `<div>${l}</div>`).join('')}
            </div>` : ''}
            ${savedPresets.length > 0 ? `
            <div class="planner-section planner-presets" style="margin-bottom:8px;">
                <div style="font-size:11px;color:var(--text-muted);margin-bottom:4px;">Quick outfit:</div>
                <div style="display:flex;flex-wrap:wrap;gap:6px;">
                    <button class="planner-preset-chip planner-preset-chip-current" data-preset-idx="-1" style="padding:4px 10px;border-radius:14px;border:1px solid rgba(255,255,255,0.25);background:rgba(255,255,255,0.05);color:var(--text-muted,#aaa);font-size:11px;cursor:default;">Current</button>
                    ${presets.map((p, i) => p && p.items ? `<button class="planner-preset-chip" data-preset-idx="${i}" style="padding:4px 10px;border-radius:14px;border:1px solid rgba(196,112,224,0.4);background:rgba(196,112,224,0.1);color:var(--text,#e0d0f0);font-size:11px;cursor:pointer;">${(p.name || `Outfit ${i+1}`).slice(0, 18)}</button>` : '').join('')}
                </div>
            </div>` : ''}
            <div class="planner-section">
                <div class="planner-toggle-row">
                    <label><input type="checkbox" id="plan-shower" ${toggles.shower ? 'checked' : ''} ${!isDirty ? 'disabled' : ''}>
                    ${_pShower !== 'planner.shower' ? _pShower : 'Shower'} ${!isDirty ? `<span style="color:var(--text-muted)">(${_pClean !== 'planner.clean' ? _pClean : 'clean'})</span>` : `<span style="color:var(--accent)">(-${_effectiveEnergyCost(5)}E)</span>`}</label>
                </div>
                <div class="planner-toggle-row">
                    <label><input type="checkbox" id="plan-makeup" ${toggles.makeup ? 'checked' : ''} ${!canMakeup ? 'disabled' : ''}>
                    ${_pMakeup !== 'planner.makeup' ? _pMakeup : 'Makeup'} ${!canMakeup ? `<span style="color:var(--text-muted)">(${_pNeedVanity !== 'planner.need_vanity' ? _pNeedVanity : 'need vanity + skill'})</span>` : `<span style="color:var(--accent)">(-${_effectiveEnergyCost(10)}E, +1 Fem)</span>`}</label>
                </div>
                ${cagePostVisible ? `
                <div class="planner-toggle-row">
                    <label><input type="checkbox" id="plan-cagepost" ${cagePostOn ? 'checked' : ''} ${!cagePostUnlocked ? 'disabled' : ''}>
                    ${_pCageLabel} ${!cagePostUnlocked ? `<span style="color:var(--text-muted)">(${_pCageNeedManualText})</span>` : `<span style="color:var(--accent)">(-${_effectiveEnergyCost(15)}E, +subs/tips daily)</span>`}</label>
                </div>` : ''}
            </div>
            <div class="planner-summary" id="planner-summary"></div>
            <div class="planner-actions">
                <button class="btn-primary" id="planner-start">${_pStartDay !== 'planner.start' ? _pStartDay : 'Start Day'}</button>
                <button class="btn-pause-secondary" id="planner-skip" title="${_pCloseHint}">
                    ${_pClose !== 'planner.close' ? _pClose : 'Skip today'}
                    <span class="planner-skip-hint">${_pCloseHint}</span>
                </button>
            </div>
        </div>
    `;

    // Outfit-preset chips: clicking applies preset via existing event handler
    // (main.js 'wardrobe:loadPreset'). Same path as wardrobe-pane preset card.
    // "Current" chip (idx == -1) is informational only — keeps equipped outfit.
    box.querySelectorAll('.planner-preset-chip').forEach(btn => {
        btn.addEventListener('click', () => {
            const idx = parseInt(btn.dataset.presetIdx, 10);
            if (idx < 0) return; // "Current" chip, no-op
            const preset = presets[idx];
            if (!preset || !preset.items) return;
            events.emit('wardrobe:loadPreset', preset.items);
        });
    });

    const updateSummary = () => {
        const s = box.querySelector('#planner-summary');
        const parts = [];
        let cost = 0;
        if (box.querySelector('#plan-shower')?.checked && isDirty) { parts.push(_pShower !== 'planner.shower' ? _pShower : 'Shower'); cost += _effectiveEnergyCost(5); }
        if (box.querySelector('#plan-makeup')?.checked && canMakeup) { parts.push(_pMakeup !== 'planner.makeup' ? _pMakeup : 'Makeup'); cost += _effectiveEnergyCost(10); }
        if (box.querySelector('#plan-cagepost')?.checked && cagePostUnlocked) {
            const _sumLabel = L.get('planner.cage_summary');
            parts.push(_sumLabel !== 'planner.cage_summary' ? _sumLabel : 'Cage post');
            cost += _effectiveEnergyCost(15);
        }
        const _noAuto = L.get('planner.no_automations');
        s.textContent = parts.length > 0 ? `${parts.join(', ')} \u2014 ${cost}E` : (_noAuto !== 'planner.no_automations' ? _noAuto : 'No automations selected');
    };

    overlay.classList.remove('hidden');
    events.emit(Events.OVERLAY_OPEN, 'overlay-planner');
    updateSummary();

    box.querySelector('#plan-shower')?.addEventListener('change', updateSummary);
    box.querySelector('#plan-makeup')?.addEventListener('change', updateSummary);
    box.querySelector('#plan-cagepost')?.addEventListener('change', updateSummary);

    const close = () => {
        layoutManager.closeOverlay('overlay-planner');
        // Safety: force backdrop hidden after animation completes
        setTimeout(() => {
            if (layoutManager.overlayStack.length === 0) {
                const bd = document.getElementById('overlay-backdrop');
                if (bd) bd.classList.add('hidden');
            }
        }, 300);
    };

    const autoExit = () => {
        // Skip routine (Day > 5) → Apartment; otherwise Get Up → Apartment.
        // Pre-fix: keyword list led with 'get dressed' which routes through
        // MorningRoutine_Wardrobe → ->-> back to MorningRoutine → #ui:planner
        // re-fires → planner re-opens → infinite loop ("X klickt und öffnet
        // sich direkt wieder", player report 2026-05-04).
        if (_autoSelectExitChoiceFn) {
            _autoSelectExitChoiceFn(['skip routine', 'get up']);
        }
    };

    box.querySelector('#planner-close').addEventListener('click', () => {
        // X = dismiss planner only; player uses inline Ink choices to
        // navigate. No autoExit — clicking X is "cancel", not "fast-exit".
        close();
    });
    box.querySelector('#planner-skip').addEventListener('click', () => {
        // Close = dismiss the overlay; leave MorningRoutine hub in place
        // so the player can use the inline choices (Shower, Apply Makeup,
        // Change outfit, Begin day) manually. No autoExit.
        close();
    });

    box.querySelector('#planner-start').addEventListener('click', () => {
        const doShower = box.querySelector('#plan-shower')?.checked && isDirty;
        const doMakeup = box.querySelector('#plan-makeup')?.checked && canMakeup;
        const cageOn = !!(box.querySelector('#plan-cagepost')?.checked && cagePostUnlocked);

        // Persist toggles for next overnight's auto-makeup logic
        localStorage.setItem('Routine_auto_shower', doShower ? '1' : '0');
        localStorage.setItem('Routine_auto_makeup', doMakeup ? '1' : '0');

        // Auto-cage-post: write directly to the Ink VAR so the morning-routine
        // body block fires on the NEXT day's overnight cycle. Set-and-forget.
        // Today's cage-post (if checked) gets auto-clicked as part of the
        // action chain below — the planner toggle handles BOTH today (chain)
        // and tomorrow (Ink VAR for the silent auto-block).
        if (cagePostVisible) {
            try { vs['auto_cage_post'] = cageOn; } catch(e) {}
        }

        // Caught 2026-05-13: "Start Day didn't actually start"
        // — pre-fix the handler only fired ONE auto-action (shower OR makeup).
        // Initial v0.2.7 chain: ran sequentially, auto-clicked every Continue,
        // then called autoExit() which jumped the player to Apartment in ~3s
        // total. Foundby:kara_young v0.2.6.7 ("when I would check the boxes
        // for morning tasks to auto complete, it would just skip through
        // multiple scenes"). Now: chain still auto-clicks Continue BETWEEN
        // tasks (so scenes stack into the panel via the MorningRoutineHub
        // family-merge), but STOPS on the FINAL task's Continue button so
        // the player reads the stacked cards + clicks Continue manually.
        close();

        const queue = [];
        if (doShower) queue.push(['shower']);
        if (doMakeup) queue.push(['apply makeup', 'makeup']);
        if (cageOn) queue.push(['post cage photo']);

        if (queue.length === 0 || !_autoSelectExitChoiceFn) {
            autoExit();
            return;
        }

        // Inject a header card so the player can tell at a glance that the
        // following stacked beats are part of one auto-routine, not three
        // separate scenes they need to interact with mid-chain. The header
        // sits at the top of the stack; the player can scroll up to it.
        try {
            const sc = document.getElementById('story-content');
            if (sc) {
                const header = document.createElement('div');
                header.className = 'story-card morning-routine-header';
                header.innerHTML = '<span style="opacity:0.7; font-style:italic;">— Morning Routine —</span>';
                header.style.cssText = 'text-align:center; padding:8px 0; margin:4px 0; border-bottom:1px solid rgba(196,160,208,0.18);';
                sc.appendChild(header);
            }
        } catch(e) { /* non-fatal */ }

        // Chain via polling — STORY_CHOICES_READY event timing is racy with
        // refreshView's STORY_APPEND + tag-emit sequence, and missing one
        // tick stalls the chain on a Continue beat. Polling every 250ms is
        // simpler and survives any refreshView ordering.
        let ticks = 0;
        const MAX_TICKS = 40; // 40 × 250ms = 10s hard cap
        // Location-family guard: the chain may only auto-click while we're
        // inside the MorningRoutine flow. Auto-skip in MorningRoutine can
        // divert to Apartment mid-chain (e.g. `auto_makeup` sets
        // has_makeup_on=true after the shower task returns, which then
        // auto-skips before the makeup task gets to fire). Without this
        // guard, the chain calls `_autoSelectExitChoiceFn(['apply makeup'])`
        // into the Apartment hub where Apply Makeup is hidden (gate fails
        // on has_makeup_on=true), tutorial-tips' click-first fallback kicks
        // in, and the chain auto-clicks an unrelated Apartment choice —
        // including the keyholder unlock entry that blasts through the
        // Vivienne/Mira/Roommate/Random scene (foundby:Anne-Sophie v0.2.7).
        const inMorningFamily = () => {
            try {
                const knot = window._sm?.currentLocation || '';
                return /^MorningRoutine|^LoftMorningRoutine/.test(knot);
            } catch (e) { return false; }
        };
        const chainStep = () => {
            if (++ticks > MAX_TICKS) return;
            // Bail if auto-skip / forced-event / overlay-divert moved us out
            // of the MorningRoutine family. Player can interact freely with
            // whatever scene they landed in.
            if (!inMorningFamily()) return;
            const btns = Array.from(document.querySelectorAll('.choice-btn:not(.disabled)'));
            if (btns.length === 0) {
                setTimeout(chainStep, 250);
                return;
            }
            // Single Continue → either chain to next task (queue non-empty)
            // or STOP here so the player reads the stacked narration.
            if (btns.length === 1) {
                const text = (btns[0].dataset.original || btns[0].textContent || '').toLowerCase();
                if (/continue/.test(text)) {
                    if (queue.length > 0) {
                        // More tasks pending — auto-click Continue to fold
                        // back to MorningRoutine hub, then fire next action.
                        btns[0].click();
                        setTimeout(chainStep, 350);
                        return;
                    }
                    // FINAL Continue — leave it for the player. They scroll
                    // through the stacked routine cards and click when ready.
                    return;
                }
            }
            // Multi-choice hub → fire next pending action or exit.
            if (queue.length > 0) {
                _autoSelectExitChoiceFn(queue.shift());
                setTimeout(chainStep, 350);
                return;
            }
            // Queue empty and we're at a multi-choice hub (no Continue beat).
            // Fall back to autoExit so the player lands at Apartment instead
            // of stranding at MorningRoutine with leftover non-routine choices.
            autoExit();
        };

        // Kick off the first action and start the polling chain.
        _autoSelectExitChoiceFn(queue.shift());
        setTimeout(chainStep, 350);
    });
}

export const plannerUI = {
    show: _showPlannerOverlay,
    setAutoSelectCallback(fn) {
        _autoSelectExitChoiceFn = fn;
    },
};
